// Path editing geometry (DRAW_PLAN §5, D3): segments, splitting (de Casteljau), the nearest point on a path,
// adding / deleting anchors, reversing, closing and joining, averaging. Anchors keep absolute handles.
import type { Anchor, Subpath } from '../model/types';

export type Seg = { a: Anchor; b: Anchor };
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Segment i of a subpath (from point i to point i+1, wrapping when closed). */
export function segment(sp: Subpath, i: number): Seg | null {
  const n = sp.points.length;
  if (i < 0 || i >= (sp.closed ? n : n - 1)) return null;
  return { a: sp.points[i]!, b: sp.points[(i + 1) % n]! };
}
export const segCount = (sp: Subpath) => (sp.closed ? sp.points.length : Math.max(0, sp.points.length - 1));

/** Point on a segment at t. */
export function pointAt(s: Seg, t: number): [number, number] {
  const u = 1 - t;
  const w0 = u * u * u,
    w1 = 3 * u * u * t,
    w2 = 3 * u * t * t,
    w3 = t * t * t;
  return [
    w0 * s.a.x + w1 * s.a.outX + w2 * s.b.inX + w3 * s.b.x,
    w0 * s.a.y + w1 * s.a.outY + w2 * s.b.inY + w3 * s.b.y,
  ];
}

/** de Casteljau split at t: the new anchor, and the updated out-handle of a / in-handle of b. */
export function splitSeg(s: Seg, t: number): { mid: Anchor; aOut: [number, number]; bIn: [number, number] } {
  const p0 = [s.a.x, s.a.y],
    p1 = [s.a.outX, s.a.outY],
    p2 = [s.b.inX, s.b.inY],
    p3 = [s.b.x, s.b.y];
  const l = (p: number[], q: number[]) => [lerp(p[0]!, q[0]!, t), lerp(p[1]!, q[1]!, t)];
  const p01 = l(p0, p1),
    p12 = l(p1, p2),
    p23 = l(p2, p3);
  const p012 = l(p01, p12),
    p123 = l(p12, p23);
  const m = l(p012, p123);
  const straight = s.a.outX === s.a.x && s.a.outY === s.a.y && s.b.inX === s.b.x && s.b.inY === s.b.y;
  const mid: Anchor = straight
    ? { x: m[0]!, y: m[1]!, inX: m[0]!, inY: m[1]!, outX: m[0]!, outY: m[1]!, type: 'corner' }
    : { x: m[0]!, y: m[1]!, inX: p012[0]!, inY: p012[1]!, outX: p123[0]!, outY: p123[1]!, type: 'smooth' };
  return {
    mid,
    aOut: straight ? [s.a.x, s.a.y] : [p01[0]!, p01[1]!],
    bIn: straight ? [s.b.x, s.b.y] : [p23[0]!, p23[1]!],
  };
}

export type Hit = { sp: number; seg: number; t: number; x: number; y: number; dist: number };

/** Nearest point on any segment of `subpaths` to (x, y): coarse sampling, then refinement. */
export function nearest(subpaths: Subpath[], x: number, y: number): Hit | null {
  let best: Hit | null = null;
  subpaths.forEach((sp, si) => {
    for (let i = 0; i < segCount(sp); i++) {
      const s = segment(sp, i)!;
      let bt = 0,
        bd = Infinity;
      for (let k = 0; k <= 24; k++) {
        const [px, py] = pointAt(s, k / 24);
        const d = (px - x) ** 2 + (py - y) ** 2;
        if (d < bd) {
          bd = d;
          bt = k / 24;
        }
      }
      // Refine by bisection on a shrinking window.
      let step = 1 / 48;
      for (let it = 0; it < 18; it++) {
        for (const t of [bt - step, bt + step]) {
          if (t < 0 || t > 1) continue;
          const [px, py] = pointAt(s, t);
          const d = (px - x) ** 2 + (py - y) ** 2;
          if (d < bd) {
            bd = d;
            bt = t;
          }
        }
        step /= 2;
      }
      const [px, py] = pointAt(s, bt);
      const dist = Math.sqrt(bd);
      if (!best || dist < best.dist) best = { sp: si, seg: i, t: bt, x: px, y: py, dist };
    }
  });
  return best;
}

/** A copy of `sp` with an anchor inserted on segment `seg` at t (the curve's shape is unchanged). */
export function insertAnchor(sp: Subpath, seg: number, t: number): Subpath {
  const s = segment(sp, seg);
  if (!s) return sp;
  const { mid, aOut, bIn } = splitSeg(s, t);
  const pts = sp.points.map((p) => ({ ...p }));
  const ia = seg,
    ib = (seg + 1) % pts.length;
  pts[ia] = { ...pts[ia]!, outX: aOut[0], outY: aOut[1] };
  pts[ib] = { ...pts[ib]!, inX: bIn[0], inY: bIn[1] };
  pts.splice(seg + 1, 0, mid);
  return { closed: sp.closed, points: pts };
}

/** Removes anchor i; the neighbouring handles stay, so the curve bridges the gap smoothly. */
export function deleteAnchor(sp: Subpath, i: number): Subpath {
  const pts = sp.points.filter((_, k) => k !== i).map((p) => ({ ...p }));
  return { closed: sp.closed && pts.length > 2, points: pts };
}

export function reverseSubpath(sp: Subpath): Subpath {
  return {
    closed: sp.closed,
    points: [...sp.points]
      .reverse()
      .map((p) => ({ ...p, inX: p.outX, inY: p.outY, outX: p.inX, outY: p.inY })),
  };
}

/** Splits subpath at anchor i (Scissors on an anchor): an open path becomes two, a closed one opens there. */
export function splitAtAnchor(sp: Subpath, i: number): Subpath[] {
  const pts = sp.points.map((p) => ({ ...p }));
  if (sp.closed) {
    const rot = [...pts.slice(i), ...pts.slice(0, i)];
    const first = rot[0]!;
    // The cut point appears at both ends of the opened path.
    const end: Anchor = { ...first, outX: first.x, outY: first.y };
    const start: Anchor = { ...first, inX: first.x, inY: first.y };
    return [{ closed: false, points: [start, ...rot.slice(1), end] }];
  }
  if (i <= 0 || i >= pts.length - 1) return [sp];
  const left = pts.slice(0, i + 1),
    right = pts.slice(i).map((p) => ({ ...p }));
  left[left.length - 1] = {
    ...left[left.length - 1]!,
    outX: left[left.length - 1]!.x,
    outY: left[left.length - 1]!.y,
  };
  right[0] = { ...right[0]!, inX: right[0]!.x, inY: right[0]!.y };
  return [
    { closed: false, points: left },
    { closed: false, points: right },
  ];
}

/** Joins two open subpaths end-to-start by their nearest endpoints (Ctrl+J); one open subpath closes. */
export function joinSubpaths(a: Subpath, b?: Subpath): Subpath {
  if (!b) return { closed: true, points: a.points.map((p) => ({ ...p })) };
  const ends = (s: Subpath) => [s.points[0]!, s.points[s.points.length - 1]!];
  const [a0, a1] = ends(a),
    [b0, b1] = ends(b);
  const d = (p: Anchor, q: Anchor) => Math.hypot(p.x - q.x, p.y - q.y);
  // Choose orientations so that a's end meets b's start.
  const options: [Subpath, Subpath, number][] = [
    [a, b, d(a1!, b0!)],
    [a, reverseSubpath(b), d(a1!, b1!)],
    [reverseSubpath(a), b, d(a0!, b0!)],
    [reverseSubpath(a), reverseSubpath(b), d(a0!, b1!)],
  ];
  options.sort((x, y) => x[2] - y[2]);
  const [x, y, gap] = options[0]!;
  const pts = [...x.points.map((p) => ({ ...p }))];
  const ypts = y.points.map((p) => ({ ...p }));
  if (gap < 1e-6) {
    // Coincident endpoints merge into one anchor.
    const last = pts[pts.length - 1]!;
    pts[pts.length - 1] = { ...last, outX: ypts[0]!.outX, outY: ypts[0]!.outY, type: 'corner' };
    ypts.shift();
  }
  return { closed: false, points: [...pts, ...ypts] };
}

/** Moves the anchors (with their handles) to their average position (Ctrl+Alt+J). */
export function averageAnchors(anchors: Anchor[]): [number, number] {
  return [
    anchors.reduce((s, p) => s + p.x, 0) / anchors.length,
    anchors.reduce((s, p) => s + p.y, 0) / anchors.length,
  ];
}

export function moveAnchor(p: Anchor, dx: number, dy: number): Anchor {
  return {
    ...p,
    x: p.x + dx,
    y: p.y + dy,
    inX: p.inX + dx,
    inY: p.inY + dy,
    outX: p.outX + dx,
    outY: p.outY + dy,
  };
}

/** Moves one handle; a smooth / symmetric anchor keeps the opposite handle on the same line. */
export function moveHandle(p: Anchor, which: 'in' | 'out', x: number, y: number, breakIt = false): Anchor {
  const q = { ...p };
  if (which === 'in') {
    q.inX = x;
    q.inY = y;
  } else {
    q.outX = x;
    q.outY = y;
  }
  if (breakIt) return { ...q, type: 'corner' };
  if (p.type === 'smooth' || p.type === 'symmetric') {
    const dx = x - p.x,
      dy = y - p.y;
    const len = Math.hypot(dx, dy) || 1;
    const otherLen =
      p.type === 'symmetric'
        ? len
        : which === 'in'
          ? Math.hypot(p.outX - p.x, p.outY - p.y)
          : Math.hypot(p.inX - p.x, p.inY - p.y);
    const ox = p.x - (dx / len) * otherLen,
      oy = p.y - (dy / len) * otherLen;
    if (which === 'in') {
      q.outX = ox;
      q.outY = oy;
    } else {
      q.inX = ox;
      q.inY = oy;
    }
  }
  return q;
}

/** Corner (handles retracted) ⇄ smooth (symmetric handles along the neighbours' direction). */
export function convertAnchor(sp: Subpath, i: number): Anchor {
  const p = sp.points[i]!;
  const hasHandles = p.inX !== p.x || p.inY !== p.y || p.outX !== p.x || p.outY !== p.y;
  if (hasHandles) return { ...p, inX: p.x, inY: p.y, outX: p.x, outY: p.y, type: 'corner' };
  const n = sp.points.length;
  const prev = sp.points[(i - 1 + n) % n]!,
    next = sp.points[(i + 1) % n]!;
  const dx = next.x - prev.x,
    dy = next.y - prev.y;
  const len = Math.hypot(dx, dy) || 1;
  const h = Math.min(Math.hypot(next.x - p.x, next.y - p.y), Math.hypot(prev.x - p.x, prev.y - p.y)) / 3;
  return {
    ...p,
    inX: p.x - (dx / len) * h,
    inY: p.y - (dy / len) * h,
    outX: p.x + (dx / len) * h,
    outY: p.y + (dy / len) * h,
    type: 'smooth',
  };
}

/** A smooth path through points (Curvature tool): Catmull-Rom tangents, converted to Bézier handles.
 *  `corners` marks indices that stay corners (no handles). */
export function curvatureSubpath(
  pts: [number, number][],
  closed: boolean,
  corners: Set<number> = new Set(),
): Subpath {
  const n = pts.length;
  const anchors: Anchor[] = pts.map(([x, y], i) => {
    if (corners.has(i) || n < 3 || (!closed && (i === 0 || i === n - 1)))
      return { x, y, inX: x, inY: y, outX: x, outY: y, type: 'corner' };
    const [px, py] = pts[(i - 1 + n) % n]!,
      [nx, ny] = pts[(i + 1) % n]!;
    const tx = (nx - px) / 6,
      ty = (ny - py) / 6;
    return { x, y, inX: x - tx, inY: y - ty, outX: x + tx, outY: y + ty, type: 'smooth' };
  });
  // Open ends: aim the end handles at the neighbour's handle so the first / last segment curves too.
  if (!closed && n >= 3) {
    const a0 = anchors[0]!,
      a1 = anchors[1]!;
    anchors[0] = { ...a0, outX: a0.x + (a1.inX - a0.x) / 2, outY: a0.y + (a1.inY - a0.y) / 2 };
    const z = anchors[n - 1]!,
      y = anchors[n - 2]!;
    anchors[n - 1] = { ...z, inX: z.x + (y.outX - z.x) / 2, inY: z.y + (y.outY - z.y) / 2 };
  }
  return { closed, points: anchors };
}
